import { ButtonBase, Stack, useTheme } from "@mui/material";
import { FoodsIcon, FoodsIconButton, FoodsSelect, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import type { BreadcrumbType } from "src/components/Breadcrumb/Breadcrumbs";
import { ScoreDetail } from "src/components/ScoreDetail/ScoreDetail";
import { ScoreDetailTabs, type Tabs } from "src/components/ScoreDetail/InnerTabs/ScoreDetailTabs";
import { useScoreDetailContext } from "src/context/ScoreDetail/useScoreDetailContext";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";
import type { ScoreWithVariation } from "src/services/ScoreService";
import { FoodsSelectTriggerAdapter } from "src/components/EntityForm/FoodsSelectTriggerAdapter";

const productScoreTabOrder = ["distribution", "evolution", "comparison"] as const;

type ProductScoreDetailProps = {
    selectedTab: Tabs;
};

type ProductV2ScoreBreadcrumbsProps = {
    breadcrumbs: BreadcrumbType[];
    fallbackTitle: string;
};

const ProductV2ScoreBreadcrumbs = ({ breadcrumbs, fallbackTitle }: ProductV2ScoreBreadcrumbsProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const currentBreadcrumb = breadcrumbs.at(-1);
    const previousBreadcrumb = breadcrumbs.at(-2);
    const scoreDrilldownAncestors = breadcrumbs.slice(1, -1);

    return (
        <Stack
            component="nav"
            aria-label={t("common.navigation.breadcrumb", "Breadcrumb")}
            direction="row"
            alignItems="center"
            minWidth={0}
            sx={{ gap: theme.spacing(1) }}
        >
            {previousBreadcrumb?.action && (
                <FoodsIconButton
                    size="m"
                    icon="arrowLeftShort"
                    buttonProps={{
                        "aria-label": t("common.back", "Back"),
                        onClick: previousBreadcrumb.action,
                    }}
                />
            )}
            <Stack direction="row" alignItems="center" minWidth={0} sx={{ flexWrap: "wrap", gap: theme.spacing(1) }}>
                {scoreDrilldownAncestors.map((breadcrumb, index) => (
                    <Stack
                        key={`${breadcrumb.label}-${index}`}
                        direction="row"
                        alignItems="center"
                        sx={{ gap: theme.spacing(1) }}
                    >
                        <ButtonBase onClick={breadcrumb.action} disabled={breadcrumb.disabled}>
                            <Typography component="span" variant="body-semibold">
                                {breadcrumb.label}
                            </Typography>
                        </ButtonBase>
                        <FoodsIcon icon="arrowRightShort" size={2} />
                    </Stack>
                ))}
                <Typography component="h1" variant="h2">
                    {currentBreadcrumb?.label || fallbackTitle}
                </Typography>
            </Stack>
        </Stack>
    );
};

export const ProductV2ScoreDetail = ({ selectedTab }: ProductScoreDetailProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const {
        score,
        scoresParams,
        selectedVariations,
        scoreWithVariations = [],
        drilldownState,
        tabConfig,
    } = useScoreDetailContext();
    const selectedVariationId = scoresParams?.query.scores?.variations?.[score.id] ?? null;
    const selectedOption = scoreWithVariations.find(({ variationId }) => variationId === selectedVariationId) ?? null;
    const distributionUrl = tabConfig.distribution.link(score.id);

    if (selectedTab === null) return <Navigate to={distributionUrl} replace />;

    const selectScoreVariation = (option: ScoreWithVariation | null) => {
        if (option === null) return;

        scoresParams?.state.selectVariation(option.scoreId, option.variationId);
        selectedVariations?.state.selectVariation(option.scoreId, option.variationId);
    };

    return (
        <Stack data-testid="product-v2-score-detail">
            <Stack
                data-testid="product-v2-score-header"
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                sx={{ gap: { xs: theme.spacing(2), sm: 0 }, paddingBlock: theme.spacing(3) }}
            >
                <ProductV2ScoreBreadcrumbs breadcrumbs={drilldownState.breadcrumbs} fallbackTitle={score.title} />
                <FoodsSelectTriggerAdapter
                    accessibleLabel={t("product.v2.scores.overview.change_variation", "Change {{score}} variation", {
                        score: score.title,
                    })}
                >
                    <FoodsSelect
                        selectedOption={selectedOption}
                        options={scoreWithVariations}
                        onChange={selectScoreVariation}
                        getId={({ rowId }) => rowId}
                        getName={({ label }) => label}
                        closeOnSelect
                        disableSearch
                        BoxOptionsProps={
                            scoreWithVariations.length === 1 ?
                                {
                                    "& > .MuiBox-root > .MuiBox-root > .MuiBox-root:empty": {
                                        display: "none",
                                    },
                                }
                            :   undefined
                        }
                        textOptions={{
                            placeholder: t("component.select.placeholder", "Select an option"),
                            searchPlaceholder: t("component.select.searchplaceholder", "Search…"),
                            emptyOptionsText: t("component.select.nooptions", "No option available"),
                            noResult: t("component.select.noresultfound", "No results found"),
                        }}
                    />
                </FoodsSelectTriggerAdapter>
            </Stack>
            <ScoreDetailTabs selectedTab={selectedTab} tabOrder={productScoreTabOrder} />
        </Stack>
    );
};

export const ProductScoreDetail = (props: ProductScoreDetailProps) => {
    const isProductUxV2 = useIsProductUxV2();

    return isProductUxV2 ? <ProductV2ScoreDetail {...props} /> : <ScoreDetail {...props} />;
};
